import { Component, OnInit, ElementRef, HostBinding, OnDestroy } from '@angular/core';
import { LabelsService } from '../../providers/labels/labels.service';
import { get } from 'lodash';
import { ValuesExchangeProvider } from '../../providers/values.exchange/values.exchange.provider';
import { Debounce } from '../../providers/debounce/debounce.decorator';
import { NavigationStart, Router } from '@angular/router';
import { find } from 'rxjs/operators';
import { Subscription, Unsubscribable } from 'rxjs';
import { TimeoutWrapperService } from '../../providers/timeout.wrapper/timeout.wrapper.service';

@Component({
  selector: 'nrwl-helpdialog',
  templateUrl: './helpdialog.component.html',
  styleUrls: ['./helpdialog.component.scss'],
  providers: [TimeoutWrapperService]
})
export class HelpDialogComponent implements OnInit, OnDestroy {
  @HostBinding('style.top') top = '0px';
  @HostBinding('style.left') left = '0px';
  @HostBinding('style.transform') transform = '';

  visible = false;
  labelObj: any; // eslint-disable-line
  labelsService: LabelsService;
  showOnLeft: boolean;

  eventHandler: any; // eslint-disable-line
  eventObj: HelpDialogEvent;
  navigationSubs: Subscription;
  exchangeUnsubs: Unsubscribable;

  constructor(
    private readonly exchange: ValuesExchangeProvider,
    private readonly router: Router,
    private timeout: TimeoutWrapperService,
    labelsService: LabelsService
  ) {
    this.labelsService = labelsService;
    //On resize event is called too early, so timeout is used
    this.eventHandler = () => this.timeout.add(this.setPositionWithDebounce.bind(this), 100);
  }

  @Debounce(100)
  setPositionWithDebounce(): void {
    this.setPosition();
  }

  ngOnInit() {
    this.exchangeUnsubs = this.exchange.registerInterest('help_dialog', this.onOpen.bind(this));
  }

  ngOnDestroy() {
    if (this.navigationSubs) {
      this.navigationSubs.unsubscribe();
    }
    this.timeout.onDestroy();
  }

  registerEvents(): void {
    window.addEventListener('scroll', this.eventHandler, true);
    window.addEventListener('resize', this.eventHandler, true);
    this.navigationSubs = this.router.events.pipe(find(e => e instanceof NavigationStart)).subscribe(this.closeDialog.bind(this));
  }

  removeEvents(): void {
    window.removeEventListener('scroll', this.eventHandler, true);
    window.removeEventListener('resize', this.eventHandler, true);
    if (this.navigationSubs) {
      this.navigationSubs.unsubscribe();
    }
  }

  onOpen(event: HelpDialogEvent): void {
    if (!this.visible) {
      this.registerEvents();
    }
    this.labelObj = this.labelsService.getHelpInfo(event.id);
    this.visible = get(event, 'visible', true);
    this.eventObj = event;
    this.setPosition();
  }

  setPosition(): void {
    if (this.eventObj.helpIconEl) {
      const windowBound = document.body.getBoundingClientRect();
      const bound = this.eventObj.helpIconEl.nativeElement.getBoundingClientRect();
      this.showOnLeft = windowBound.right / 2 < bound.left;
      this.top = bound.top + 'px';
      if (this.showOnLeft) {
        this.left = bound.right + 'px';
        this.transform = 'translateX(-100%)';
      } else {
        this.left = bound.left + 'px';
        this.transform = '';
      }

      this.hideIfIconIsNotVisible(bound);
    }
  }

  // eslint-disable-next-line
  hideIfIconIsNotVisible(bound: any): void {
    const isIconVisibleOnScreen = bound.top >= 0 && bound.top <= window.innerHeight && bound.left >= 0 && bound.left <= window.innerWidth;

    if (!isIconVisibleOnScreen) {
      this.closeDialog();
    }
  }

  closeDialog(): void {
    this.exchange.notifyListeners('help_dialog', { visible: false, helpIconEl: null });
    this.removeEvents();
  }
}

export interface HelpDialogEvent {
  id: string;
  visible?: boolean;
  helpIconEl: ElementRef<any>; // eslint-disable-line
}
